import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Droplet, Gauge, HandCoins, HeartPulse, Pill, Scale, Stethoscope } from 'lucide-react';
import { Sheet } from '../components/Sheet';
import { useCanWrite } from '../account/useCanWrite';
import { AccessCheckForm, BpForm, FluidForm, SymptomForm, WeightForm } from '../features/tracking/EntryForms';
import { DosesToday, useTodayDoses } from '../features/meds/DosesToday';
import { Button } from '../components/Button';
import { ExpenseForm } from '../features/expenses/ExpenseForm';
import type { Expense } from '../data/expenses';
import type { AccessCheck, BpEntry, FluidEntry, SymptomEntry, WeightEntry } from '../data/tracking';

/** Everything the quick-log sheet can open, with the entry being edited (if any). */
export type LogTarget =
  | { kind: 'fluid'; entry?: FluidEntry }
  | { kind: 'weight'; entry?: WeightEntry }
  | { kind: 'bp'; entry?: BpEntry }
  | { kind: 'symptom'; entry?: SymptomEntry; sessionId?: string }
  | { kind: 'access'; entry?: AccessCheck }
  | { kind: 'med' }
  | { kind: 'expense'; entry?: Expense };

type LogKind = LogTarget['kind'];

const TITLES: Record<LogKind, [string, string]> = {
  fluid: ['Log a drink', 'Edit drink'],
  weight: ['Log weight', 'Edit weight'],
  bp: ['Log blood pressure', 'Edit blood pressure'],
  symptom: ['Log a symptom', 'Edit symptom'],
  access: ['Check your access', 'Edit access check'],
  med: ['Medicines today', 'Medicines today'],
  expense: ['Log an expense', 'Edit expense']
};

const entries: { label: string; icon: typeof Droplet; kind?: LogKind; to?: string }[] = [
  { label: 'Fluid', icon: Droplet, kind: 'fluid' },
  { label: 'Weight', icon: Scale, kind: 'weight' },
  { label: 'Blood pressure', icon: Gauge, kind: 'bp' },
  { label: 'Symptom', icon: HeartPulse, kind: 'symptom' },
  { label: 'Session', icon: Activity, to: '/sessions/new' },
  { label: 'Med taken', icon: Pill, kind: 'med' },
  { label: 'Access check', icon: Stethoscope, kind: 'access' },
  { label: 'Expense', icon: HandCoins, kind: 'expense' }
];

interface QuickLogValue {
  /** Opens the list of things to log, or straight to one form (to edit `entry`, if given). */
  open: (target?: LogTarget) => void;
}

const QuickLogContext = createContext<QuickLogValue | null>(null);

export function useQuickLog() {
  const value = useContext(QuickLogContext);
  if (!value) throw new Error('useQuickLog() must be used inside <QuickLogProvider>.');
  return value;
}

type SheetState = { view: 'closed' } | { view: 'menu' } | { view: 'form'; target: LogTarget; fromMenu: boolean };

export function QuickLogProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SheetState>({ view: 'closed' });
  const open = useCallback((target?: LogTarget) => setState(target ? { view: 'form', target, fromMenu: false } : { view: 'menu' }), []);
  const close = useCallback(() => setState({ view: 'closed' }), []);
  const value = useMemo(() => ({ open }), [open]);

  return (
    <QuickLogContext.Provider value={value}>
      {children}
      {state.view === 'menu' ? <LogMenu onClose={close} onPick={(target) => setState({ view: 'form', target, fromMenu: true })} /> : null}
      {state.view === 'form' ? (
        <Sheet
          title={TITLES[state.target.kind]['entry' in state.target && state.target.entry ? 1 : 0]}
          onClose={close}
          onBack={state.fromMenu ? () => setState({ view: 'menu' }) : undefined}
        >
          <LogForm target={state.target} onDone={close} />
        </Sheet>
      ) : null}
    </QuickLogContext.Provider>
  );
}

function LogForm({ target, onDone }: { target: LogTarget; onDone: () => void }) {
  switch (target.kind) {
    case 'fluid':
      return <FluidForm entry={target.entry} onDone={onDone} />;
    case 'weight':
      return <WeightForm entry={target.entry} onDone={onDone} />;
    case 'bp':
      return <BpForm entry={target.entry} onDone={onDone} />;
    case 'symptom':
      return <SymptomForm entry={target.entry} sessionId={target.sessionId} onDone={onDone} />;
    case 'access':
      return <AccessCheckForm entry={target.entry} onDone={onDone} />;
    case 'med':
      return <MedTaken onDone={onDone} />;
    case 'expense':
      return <ExpenseForm entry={target.entry} onDone={onDone} />;
  }
}

function MedTaken({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const { hasMedications } = useTodayDoses();
  if (!hasMedications) {
    return (
      <div className="space-y-3 text-sm">
        <p className="text-clinical-muted">Add your medicines first, then mark doses taken here or on Today.</p>
        <Button
          className="w-full"
          onClick={() => {
            onDone();
            navigate('/health/meds/new');
          }}
        >
          Add a medicine
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <DosesToday />
      <Button variant="secondary" className="w-full" onClick={onDone}>
        Done
      </Button>
    </div>
  );
}

/** Bottom sheet listing everything that can be logged. */
function LogMenu({ onClose, onPick }: { onClose: () => void; onPick: (target: LogTarget) => void }) {
  const navigate = useNavigate();
  const canWrite = useCanWrite();

  return (
    <Sheet title="Log" onClose={onClose}>
      <ul className="grid grid-cols-4 gap-2">
        {entries.map(({ label, icon: Icon, kind, to }) => (
          <li key={label}>
            <button
              type="button"
              disabled={(!kind && !to) || !canWrite}
              title={kind || to ? undefined : 'Coming soon'}
              onClick={() => {
                if (kind) {
                  onPick({ kind });
                } else if (to) {
                  onClose();
                  navigate(to);
                }
              }}
              className="focus-ring flex min-h-20 w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-clinical-line p-2 text-center text-xs font-medium hover:bg-clinical-surface disabled:text-clinical-muted disabled:opacity-60"
            >
              <Icon className="h-6 w-6 text-clinical-primary" />
              {label}
            </button>
          </li>
        ))}
      </ul>
      {canWrite ? null : <p className="mt-3 text-center text-xs text-clinical-muted">Your plan has lapsed, so logging is paused.</p>}
    </Sheet>
  );
}
